import _ from 'lodash';
import {
  AllocationsGridColumnName,
  DesignationsGridColumnName
} from '../../grid/types/column.types';
import { SortOrder } from '../../grid/types/sorting.types';
import {
  determineDesignationStatus,
  getBreakdownsForAllotment,
  getCredit,
  getIssuesMapper,
  getLatestModifiedDate
} from '../../helpers/designations.helper';
import { Allotment } from '../../types/allotment.types';
import {
  DesignationBreakdown,
  DesignationDealInfo,
  DesignationIssue,
  DesignationsCardTypes,
  PriorityPolicy
} from '../../types/designation.types';
import { PRIORITY_RULES_VIOLATION_CODE } from '../../utils/breakdowns.const';
import { DesignationActionTypes, DesignationActionUnion } from './actions';
import { AllotmentState, DesignationState } from './state';

export const initialDesignationState: DesignationState = {
  cards: [
    {
      key: DesignationsCardTypes.DesignationsActive,
      header: 'Designations Active',
      collapsed: false,
      sortModel: [
        {
          colId: DesignationsGridColumnName.DealDate,
          sort: SortOrder.Asc
        }
      ]
    },
    {
      key: DesignationsCardTypes.DesignationsFinal,
      header: 'Designations Final',
      collapsed: false,
      sortModel: [
        {
          colId: DesignationsGridColumnName.DealDate,
          sort: SortOrder.Asc
        }
      ]
    }
  ],
  selectedAllotments: undefined,
  hasDifferentBreakdowns: false,
  allotment: {
    allotments: undefined,
    loading: false,
    loaded: false,
    error: undefined
  },
  issue: {
    issues: undefined,
    loading: false,
    loaded: false,
    error: undefined
  },
  dealInfoState: {
    dealInfo: undefined,
    loading: false,
    loaded: false,
    error: undefined,
    submissionError: undefined,
    isSubmitting: false
  },
  breakdown: {
    breakdowns: undefined,
    loading: false,
    loaded: false,
    error: undefined,
    submissionError: null
  },
  desiEntryData: {
    isBuysideFirmSetUpInCasa: undefined,
    allowBuysideDesiEntry: undefined,
    areAllotmentsReleased: undefined,
    areDesignationsFinal: undefined,
    isDealerBuysideConnected: undefined,
    loading: false,
    loaded: false,
    ignoreDisableTab: false,
    error: undefined
  },
  isBreakdownFormDirty: false,
  activeTab: 'Summary'
};

export function designationsReducer(
  state: DesignationState = initialDesignationState,
  action: DesignationActionUnion
): DesignationState {
  switch (action.type) {
    case DesignationActionTypes.ClearDesiData:
      return {
        ...state,
        allotment: {
          allotments: undefined,
          loading: false,
          loaded: false,
          error: undefined
        },
        dealInfoState: {
          dealInfo: undefined,
          loading: false,
          loaded: false,
          error: undefined,
          submissionError: undefined,
          isSubmitting: false
        },
        breakdown: {
          breakdowns: undefined,
          loading: false,
          loaded: false,
          error: undefined,
          submissionError: null
        },
        selectedAllotments: undefined,
        hasDifferentBreakdowns: false,
        isBreakdownFormDirty: false
      };

    case DesignationActionTypes.LoadAllotments: {
      return {
        ...state,
        allotment: {
          ...state.allotment,
          allotments: [],
          loading: true
        }
      };
    }

    case DesignationActionTypes.LoadAllotmentsSuccess:
      return {
        ...state,
        allotment: {
          allotments: action.payload,
          loading: false,
          loaded: true,
          error: undefined
        }
      };

    case DesignationActionTypes.LoadAllotmentsFailure:
      return {
        ...state,
        allotment: {
          allotments: undefined,
          loading: false,
          loaded: false,
          error: action.payload
        }
      };

    case DesignationActionTypes.LoadDesignationIssues: {
      return {
        ...state,
        issue: {
          ...state.issue,
          loading: true,
          loaded: false
        }
      };
    }

    case DesignationActionTypes.LoadDesignationIssuesSuccess:
      return {
        ...state,
        issue: {
          issues: getIssuesMapper(action.payload),
          loading: false,
          loaded: true,
          error: undefined
        }
      };

    case DesignationActionTypes.LoadDesignationIssuesFailure:
      return {
        ...state,
        issue: {
          issues: undefined,
          loading: false,
          loaded: false,
          error: action.payload
        }
      };

    case DesignationActionTypes.DesignationsSetCardSortModel:
      return {
        ...state,
        cards: buildDesignationCardsSortModel(state, action)
      };

    case DesignationActionTypes.LoadDesignationDealInfo: {
      return {
        ...state,
        dealInfoState: {
          ...state.dealInfoState,
          loading: true
        }
      };
    }

    case DesignationActionTypes.LoadDesignationDealInfoSuccess:
      return {
        ...state,
        dealInfoState: {
          ...state.dealInfoState,
          dealInfo: action.payload,
          loading: false,
          loaded: true,
          error: undefined
        }
      };

    case DesignationActionTypes.LoadDesignationDealInfoFailure:
      return {
        ...state,
        dealInfoState: {
          ...state.dealInfoState,
          dealInfo: undefined,
          loading: false,
          loaded: false,
          error: action.payload
        }
      };

    case DesignationActionTypes.UpdateSelectedAllotments:
      return {
        ...state,
        selectedAllotments: action.selectedAllotments
      };

    case DesignationActionTypes.SubmitDesignationBreakdown:
      return {
        ...state,
        dealInfoState: {
          ...state.dealInfoState,
          loading: true,
          loaded: false,
          error: undefined,
          isSubmitting: true,
          submissionError: undefined
        },
        breakdown: {
          ...state.breakdown,
          submissionError: null
        }
      };

    case DesignationActionTypes.SubmitDesignationBreakdownSuccess: {
      const breakdownsByAllotmentId = _.keyBy(
        action.payload,
        (breakdown: DesignationBreakdown) => breakdown.allotmentId
      );
      const updatedBreakdowns = _.map(
        state.breakdown.breakdowns,
        (breakdown) => {
          const breakdownAllotmentId = breakdown.allotmentId;
          const updatedBreakdown =
            breakdownsByAllotmentId[breakdownAllotmentId];
          if (updatedBreakdown) {
            return updatedBreakdown;
          }
          return breakdown;
        }
      );
      return {
        ...state,
        breakdown: {
          ...state.breakdown,
          breakdowns: updatedBreakdowns
        },
        dealInfoState: {
          ...state.dealInfoState,
          loading: false,
          loaded: true,
          error: undefined
        }
      };
    }

    case DesignationActionTypes.SubmitDesignationBreakdownFailure:
      return {
        ...state,
        dealInfoState: {
          ...state.dealInfoState,
          loading: false,
          loaded: true,
          submissionError: action.payload
        }
      };

    case DesignationActionTypes.OpenSubmissionConflictErrorModal:
      return {
        ...state,
        dealInfoState: {
          ...state.dealInfoState,
          loading: false,
          loaded: true
        }
      };

    case DesignationActionTypes.SetDesignationBreakdownFailureError:
      return {
        ...state,
        dealInfoState: {
          ...state.dealInfoState,
          loading: false,
          loaded: true
        },
        breakdown: {
          ...state.breakdown,
          submissionError: action.payload
        }
      };

    case DesignationActionTypes.ResetDesignationBreakdownFailureError:
      return {
        ...state,
        breakdown: {
          ...state.breakdown,
          submissionError: null
        }
      };

    case DesignationActionTypes.CompleteDesignationBreakdownSubmission:
      return {
        ...state,
        dealInfoState: {
          ...state.dealInfoState,
          isSubmitting: false
        }
      };

    case DesignationActionTypes.LoadBreakdown: {
      return {
        ...state,
        breakdown: {
          ...state.breakdown,
          loading: true
        }
      };
    }

    case DesignationActionTypes.LoadBreakdownSuccess:
      return {
        ...state,
        breakdown: {
          breakdowns: action.data,
          loading: false,
          loaded: true,
          error: undefined
        }
      };

    case DesignationActionTypes.LoadBreakdownFailure:
      return {
        ...state,
        breakdown: {
          breakdowns: undefined,
          loading: false,
          loaded: false,
          error: action.payload
        }
      };

    case DesignationActionTypes.LoadDesiEntryStatus: {
      return {
        ...state,
        desiEntryData: {
          ...initialDesignationState.desiEntryData,
          loading: true,
          loaded: false,
          ignoreDisableTab: action.ignoreDisableTab
        }
      };
    }

    case DesignationActionTypes.LoadDesiEntryStatusSuccess:
      return {
        ...state,
        desiEntryData: {
          ...state.desiEntryData,
          ...action.data,
          loading: false,
          loaded: true,
          ignoreDisableTab: false,
          error: undefined
        }
      };

    case DesignationActionTypes.LoadDesiEntryStatusFailure:
      return {
        ...state,
        desiEntryData: {
          isBuysideFirmSetUpInCasa: undefined,
          allowBuysideDesiEntry: undefined,
          areAllotmentsReleased: undefined,
          areDesignationsFinal: undefined,
          isDealerBuysideConnected: undefined,
          loading: false,
          loaded: false,
          ignoreDisableTab: false,
          error: action.payload
        }
      };

    case DesignationActionTypes.UpdateHasDifferentBreakdowns:
      return {
        ...state,
        hasDifferentBreakdowns: action.hasDifferentBreakdowns
      };

    case DesignationActionTypes.UpdateDesignationStatus: {
      const allotmentData = state.allotment?.allotments || [];
      const breakdownData = state.breakdown.breakdowns;
      const hasDataToUpdate = allotmentData.length > 0 && !!breakdownData;

      const updatedAllotments = hasDataToUpdate
        ? allotmentData.map((allotment: Allotment) => {
            const breakdownsForAllotment = getBreakdownsForAllotment(
              allotment.id,
              breakdownData
            );

            const updatedAllotment: Allotment = {
              ...allotment,
              lastModifiedRoleCode:
                breakdownsForAllotment.length > 1
                  ? 'SYN'
                  : breakdownsForAllotment[0]?.lastModifiedByRoleTypeCode
            };

            updatedAllotment[AllocationsGridColumnName.BreakdownLastModified] =
              getLatestModifiedDate(breakdownsForAllotment);

            updatedAllotment[AllocationsGridColumnName.DesignationStatus] =
              determineDesignationStatus(
                allotment.amount,
                breakdownsForAllotment
              );

            updatedAllotment.credit = getCredit(
              breakdownsForAllotment,
              updatedAllotment
            );

            return updatedAllotment;
          })
        : [...allotmentData];

      return {
        ...state,
        allotment: {
          ...state.allotment,
          allotments: updatedAllotments
        }
      };
    }

    case DesignationActionTypes.UpdateDesiBreakdownFormIsDirty:
      return {
        ...state,
        isBreakdownFormDirty: action.isFormDirty
      };

    case DesignationActionTypes.SetActiveTab: {
      return {
        ...state,
        activeTab: action.payload
      };
    }

    case DesignationActionTypes.ResetActiveTab: {
      return {
        ...state,
        activeTab: 'Summary'
      };
    }

    case DesignationActionTypes.SaveFormattedDesignationIssues: {
      const { issues, issueStatusType } = action.payload;

      return {
        ...state,
        issue: {
          ...state.issue,
          [issueStatusType]: issues
        }
      };
    }

    default:
      return state;
  }
}

export const getAllotmentsData = (state: DesignationState): AllotmentState =>
  state.allotment;

export const getAllotments = (state: DesignationState): Allotment[] =>
  state.allotment.allotments;

export const getAllotmentsLoadingState = (state: DesignationState): boolean =>
  state.allotment.loading;

export const getDesignationIssues = (
  state: DesignationState
): DesignationIssue[] => state.issue.issues;

export const getDesignationIssuesLoadingState = (
  state: DesignationState
): boolean => state.issue.loading;

export const getDesignationDealInfo = (
  state: DesignationState
): DesignationDealInfo => state.dealInfoState.dealInfo;

export const getDesignationDealPriorityPolicy = (
  state: DesignationState
): PriorityPolicy => state.dealInfoState.dealInfo?.priorityPolicy;

export const getDesignationDealInfoLoadingState = (
  state: DesignationState
): boolean => state.dealInfoState?.loading;

export const getDesignationDealInfoLoadedState = (
  state: DesignationState
): boolean => state.dealInfoState?.loaded;

export const getSelectedAllotments = (state: DesignationState): Allotment[] =>
  state.selectedAllotments;

export const getBreakdownFormDirtyState = (state: DesignationState): boolean =>
  state.isBreakdownFormDirty;

export const getBreakdowns = (
  state: DesignationState
): DesignationBreakdown[] => state.breakdown.breakdowns;

export const getAllotmentsBreakdownsLoadingState = (
  state: DesignationState
): boolean => state.breakdown?.loading || state.allotment?.loading;

export const getDesignationsFinalStatus = (state: DesignationState): boolean =>
  state.desiEntryData.areDesignationsFinal;

export const getBreakdownsSubmissionError = (state: DesignationState) =>
  state.breakdown?.submissionError;

export const hasBreakdownsSubmissionError = (state: DesignationState) =>
  state.breakdown?.submissionError?.errorCode === PRIORITY_RULES_VIOLATION_CODE;

export const getAllotmentsHasDifferentBreakdowns = (
  state: DesignationState
): boolean => state.hasDifferentBreakdowns;

export const selectDesignationsCardSortModel =
  (key: DesignationsCardTypes) => (state: DesignationState) => {
    const card = state.cards.find((c) => c.key === key);
    return card ? card.sortModel : undefined;
  };

function buildDesignationCardsSortModel(state, action) {
  return state.cards.map((card) =>
    card.key === action.payload.key
      ? { ...card, sortModel: action.payload.sortModel }
      : card
  );
}
export const isDesiApisLoaded = (state: DesignationState): boolean =>
  state.allotment.loaded &&
  state.dealInfoState.loaded &&
  state.breakdown.loaded;

export const isDesiApisHasError = (state: DesignationState): boolean =>
  !!state.allotment.error ||
  !!state.dealInfoState.error ||
  !!state.breakdown.error;

export const isDesiBreakdownsSubmitting = (state: DesignationState): boolean =>
  state.dealInfoState.isSubmitting;

export const getIssuesByDesignationStatus = (
  status: DesignationsCardTypes,
  state: DesignationState
): DesignationIssue[] =>
  state.issue.issues?.filter(
    (issue) =>
      issue.designationStatus === status &&
      !(issue.isDayToDay || issue.isTBD || issue.isWeekOf)
  ) || [];
